import React, { ChangeEvent, useState } from "react";
import {
  Button,
  Card,
  Controller,
  ErrorMessage,
  Header,
  Input,
  parsePhoneNumbersFromStrings,
  Select,
  SwitchInput,
  useCustomForm,
  useProjectsOptionsQuery,
} from "@";
import { defaultValues, schema } from "../../lib";
import {
  useCalculateUsersCountMutation,
  useCreateWhatsappCampaignMutation,
  useWhatsappTemplatesQuery,
} from "../../api";
import type { CreateCampaignFormData, SelectOption } from "../../types";

interface BreadcrumbLink {
  name: string;
  link: string;
}

const Page: React.FC = () => {
  const [phoneNumbersInput, setPhoneNumbersInput] = useState<string>("");
  const [excludePhoneNumbersInput, setExcludePhoneNumbersInput] =
    useState<string>("");

  // Breadcrumb links
  const links: BreadcrumbLink[] = [
    { name: "حملات WhatsApp", link: "/whatsapp-campaign" },
    { name: "إنشاء حملة", link: "" },
  ];

  // Fetch templates
  const { data: templatesOptions } = useWhatsappTemplatesQuery();

  // Fetch projects list
  const { data: projectsOptions } = useProjectsOptionsQuery();

  // Create mutation
  const { mutateAsync: createCampaign } = useCreateWhatsappCampaignMutation();

  // Calculate users count mutation
  const {
    mutateAsync: calculateCount,
    isPending: isCalculating,
    data: usersCount,
  } = useCalculateUsersCountMutation();

  // Form hook
  const {
    errors,
    control,
    setValue,
    watch,
    register,
    handleSubmit,
    isSubmitting,
    getValues,
  } = useCustomForm(defaultValues, schema);

  // Form submit handler
  const submit = async (data: CreateCampaignFormData) => {
    try {
      await createCampaign(data);
    } catch (error) {
      console.error("Error creating WhatsApp campaign:", error);
    }
  };

  return (
    <>
      {/* Header */}
      <Header title="إنشاء حملة WhatsApp" links={links} />
      <form onSubmit={handleSubmit(submit)} className="relative max-w-6xl">
        {/* Campaign Info */}
        <Card title="معلومات الحملة">
          <Input
            style="mb-5"
            label="اسم الحملة"
            error={errors.name}
            input={register("name")}
          />

          <div className="mb-5">
            <label className="input-label">
              القالب <span className="star">*</span>
            </label>
            <Controller
              name="template"
              control={control}
              render={({ field }) => (
                <Select
                  isClearable
                  options={templatesOptions}
                  value={templatesOptions?.find(
                    (opt: SelectOption) => opt.value === field.value,
                  )}
                  onChange={(option: SelectOption | null) =>
                    field.onChange(option || null)
                  }
                  placeholder="اختر القالب"
                />
              )}
            />
            <ErrorMessage value={errors.template?.message} />
          </div>

          <Input
            rows={12}
            type="textarea"
            label="الوصف"
            error={errors.description}
            input={register("description")}
          />
        </Card>

        {/* Filter Criteria */}
        <Card isOpen={false} title="معايير التصفية">
          <div className="grid gap-5 md:grid-cols-1">
            {/* specificProjectIds */}
            <div>
              <label className="input-label">المشاريع المحددة</label>
              <Controller
                control={control}
                name="specificProjectIds"
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={(projectsOptions || []).filter((c: SelectOption) =>
                      value?.includes(c.value),
                    )}
                    placeholder="اختر المشاريع"
                    onChange={(val: readonly SelectOption[]) =>
                      onChange(val.map((c) => c.value))
                    }
                    options={projectsOptions}
                    isMulti
                  />
                )}
              />
              <ErrorMessage value={errors.specificProjectIds?.message} />
            </div>

            {/* Donation Date Range */}
            <Input
              star
              showTimeSelect
              type="date-range"
              labelA="تاريخ التبرع من"
              labelB="تاريخ التبرع إلى"
              valueA={watch("donationDateFrom")}
              valueB={watch("donationDateTo")}
              changeA={(e: string) => setValue("donationDateFrom", e)}
              changeB={(e: string) => setValue("donationDateTo", e)}
            />

            {/* Abandoned Cart Date Range */}
            <Input
              star
              showTimeSelect
              type="date-range"
              labelA="تاريخ السلة المتروكة من"
              labelB="تاريخ السلة المتروكة إلى"
              valueA={watch("abandonedCartDateFrom")}
              valueB={watch("abandonedCartDateTo")}
              changeA={(e: string) => setValue("abandonedCartDateFrom", e)}
              changeB={(e: string) => setValue("abandonedCartDateTo", e)}
            />

            {/* Exclude Donated Date Range */}
            <Input
              star
              showTimeSelect
              type="date-range"
              labelA="استثناء المتبرعين من تاريخ"
              labelB="استثناء المتبرعين إلى تاريخ"
              valueA={watch("excludeDonatedFromDate")}
              valueB={watch("excludeDonatedToDate")}
              changeA={(e: string) => setValue("excludeDonatedFromDate", e)}
              changeB={(e: string) => setValue("excludeDonatedToDate", e)}
            />

            {/* Abandoned Cart Amount Range */}
            <Input
              star
              type="number-range"
              labelA="الحد الأدنى لمبلغ السلة المتروكة"
              labelB="الحد الأقصى لمبلغ السلة المتروكة"
              valueA={watch("minAbandonedCartAmount")}
              valueB={watch("maxAbandonedCartAmount")}
              changeA={(e: ChangeEvent<HTMLInputElement>) =>
                setValue("minAbandonedCartAmount", e.target.value)
              }
              changeB={(e: ChangeEvent<HTMLInputElement>) =>
                setValue("maxAbandonedCartAmount", e.target.value)
              }
            />

            {/* Donation Amount Range */}
            <Input
              star
              type="number-range"
              labelA="الحد الأدنى لمبلغ التبرع"
              labelB="الحد الأقصى لمبلغ التبرع"
              valueA={watch("minDonationAmount")}
              valueB={watch("maxDonationAmount")}
              changeA={(e: ChangeEvent<HTMLInputElement>) =>
                setValue("minDonationAmount", e.target.value)
              }
              changeB={(e: ChangeEvent<HTMLInputElement>) =>
                setValue("maxDonationAmount", e.target.value)
              }
            />

            {/* Specific Phone Numbers */}
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <label className="input-label">أرقام هواتف محددة</label>
                <textarea
                  rows={4}
                  className="input"
                  value={phoneNumbersInput}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                    setPhoneNumbersInput(e.target.value);
                    setValue(
                      "specificPhoneNumbers",
                      parsePhoneNumbersFromStrings(e.target.value),
                    );
                  }}
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  يمكنك إدخال عدة أرقام (كل رقم في سطر جديد أو مفصولة بفواصل)
                </p>
              </div>

              {/* Exclude Phone Numbers */}
              <div>
                <label className="input-label">أرقام الهواتف المستثناة</label>
                <textarea
                  rows={4}
                  className="input"
                  value={excludePhoneNumbersInput}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                    setExcludePhoneNumbersInput(e.target.value);
                    setValue(
                      "excludePhoneNumbers",
                      parsePhoneNumbersFromStrings(e.target.value),
                    );
                  }}
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  أرقام الهواتف التي سيتم استثناؤها من الحملة (كل رقم في سطر
                  جديد أو مفصولة بفواصل)
                </p>
              </div>
            </div>

            {/* Has Abandoned Cart */}
            <div className="grid gap-5 md:grid-cols-2">
              <SwitchInput
                label="لديه سلة متروكة"
                checked={watch("hasAbandonedCart")}
                change={(value: boolean) => setValue("hasAbandonedCart", value)}
              />

              {/* Is Active */}
              <SwitchInput
                label="نشط"
                checked={watch("isActive")}
                change={(value: boolean) => setValue("isActive", value)}
              />
            </div>
          </div>

          {/* Calculate Users Count Section */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              title="حساب عدد المستخدمين"
              isLoading={isCalculating}
              onClick={() => calculateCount(getValues())}
              className="btn-green !w-auto"
            />

            {usersCount?.toString() && (
              <div className="rounded-2xl bg-green-100 px-4 py-2 text-green-800 dark:bg-green-900 dark:text-green-200">
                <span className="font-semibold">
                  عدد المستخدمين المستهدفين في الحملة:{" "}
                </span>
                <span className="text-lg font-bold">{usersCount}</span>
              </div>
            )}
          </div>
        </Card>

        {/* Submit Button */}
        <div className="floating-btn">
          <Button type="submit" title="إنشاء الحملة" isLoading={isSubmitting} />
        </div>
      </form>
    </>
  );
};

export default Page;
